/*
 * Copyright 2026 OpenTrace Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { openDB, type IDBPDatabase } from 'idb';
import type {
  ChatHistoryStore,
  Conversation,
  ConversationSummary,
} from './chatHistoryStore';
import type { ChatMessage } from './providers';
import type {
  Attachment,
  ImageAttachment,
  FileAttachment,
} from '../components/chat/types';

// Re-export types so existing consumers don't need to change imports
export type { Conversation, ConversationSummary, ChatHistoryStore };

const DB_NAME = 'opentrace_chat';
const DB_VERSION = 3;
const STORE_NAME = 'conversations';
const BLOB_STORE = 'image_blobs';

interface StoredBlob {
  /** Same as Attachment.id */
  id: string;
  conversationId: string;
  dataUrl: string;
  /** For file attachments, stores the text content */
  textContent?: string;
}

/** Placeholder used when the blob is missing (deleted externally, etc.) */
const MISSING_IMAGE_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60">' +
      '<rect width="60" height="60" fill="#333" rx="6"/>' +
      '<text x="30" y="34" text-anchor="middle" fill="#888" font-size="11">?</text>' +
      '</svg>',
  );

export function generateId(): string {
  return crypto.randomUUID();
}

export function titleFromFirstMessage(messages: ChatMessage[]): string {
  const first = messages.find((m) => m.role === 'user');
  if (!first) return 'New conversation';
  // Use only the first line, strip markdown formatting
  const text = first.content
    .trim()
    .split('\n')[0]
    .replace(/[#*_`~>]/g, '');
  if (text.length <= 50) return text || 'New conversation';
  return text.slice(0, 47) + '...';
}

/**
 * IndexedDB-backed implementation of ChatHistoryStore.
 * Stores conversations entirely in the user's browser.
 */
export class IDBChatHistoryStore implements ChatHistoryStore {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  private getDB(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, _newVersion, transaction) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            store.createIndex('updatedAt', 'updatedAt');
            store.createIndex('projectKey', 'projectKey');
          } else if (oldVersion < 2) {
            const store = transaction.objectStore(STORE_NAME);
            if (!store.indexNames.contains('projectKey')) {
              store.createIndex('projectKey', 'projectKey');
            }
          }
          // v3: separate blob store for image attachments
          if (oldVersion < 3) {
            if (!db.objectStoreNames.contains(BLOB_STORE)) {
              const blobStore = db.createObjectStore(BLOB_STORE, {
                keyPath: 'id',
              });
              blobStore.createIndex('conversationId', 'conversationId');
            }
            // Note: we intentionally skip migrating existing inline images here.
            // Using async operations (.then/.getAll) inside the upgrade callback
            // can cause TransactionInactiveError. Inline images from before v3
            // will continue to work as-is; new images use the blob store.
          }
        },
      }).catch((err) => {
        this.dbPromise = null;
        throw err;
      });
    }
    return this.dbPromise;
  }

  /**
   * Extract blobs from messages and return stripped messages + blobs.
   */
  private stripBlobs(
    convId: string,
    messages: ChatMessage[],
  ): { stripped: ChatMessage[]; blobs: StoredBlob[] } {
    const blobs: StoredBlob[] = [];
    const stripped = messages.map((m) => {
      if (m.role !== 'user') return m;
      let result = m;

      // Strip legacy image blobs
      if (m.images?.length) {
        result = {
          ...result,
          images: m.images.map((img) => {
            if (img.dataUrl) {
              blobs.push({
                id: img.id,
                conversationId: convId,
                dataUrl: img.dataUrl,
              });
            }
            return { ...img, dataUrl: '' };
          }),
        };
      }

      // Strip attachment blobs
      if (m.attachments?.length) {
        result = {
          ...result,
          attachments: m.attachments.map((att) => {
            if (att.kind === 'image' && att.dataUrl) {
              blobs.push({
                id: att.id,
                conversationId: convId,
                dataUrl: att.dataUrl,
              });
              return { ...att, dataUrl: '' };
            } else if (att.kind === 'file' && att.textContent) {
              blobs.push({
                id: att.id,
                conversationId: convId,
                dataUrl: '',
                textContent: att.textContent,
              });
              return { ...att, textContent: '' };
            }
            return att;
          }),
        };
      }

      return result;
    });
    return { stripped, blobs };
  }

  /**
   * Re-hydrate blobs from the blob store into messages.
   * Uses getAllFromIndex to fetch all blobs for the conversation in one call.
   */
  private async hydrateBlobs(
    db: IDBPDatabase,
    convId: string,
    messages: ChatMessage[],
  ): Promise<ChatMessage[]> {
    // Check if anything needs hydrating
    let needsHydration = false;
    for (const m of messages) {
      if (m.role !== 'user') continue;
      if (m.images) {
        for (const img of m.images) {
          if (!img.dataUrl) {
            needsHydration = true;
            break;
          }
        }
      }
      if (!needsHydration && m.attachments) {
        for (const att of m.attachments) {
          const kind = att.kind ?? ('dataUrl' in att ? 'image' : 'file');
          if (
            (kind === 'image' &&
              !('dataUrl' in att && (att as ImageAttachment).dataUrl)) ||
            (kind === 'file' &&
              !('textContent' in att && (att as FileAttachment).textContent))
          ) {
            needsHydration = true;
            break;
          }
        }
      }
      if (needsHydration) break;
    }
    if (!needsHydration) return messages;

    // Fetch all blobs for this conversation in one call
    const allBlobs: StoredBlob[] = await db.getAllFromIndex(
      BLOB_STORE,
      'conversationId',
      convId,
    );
    const blobMap = new Map<string, StoredBlob>();
    for (const blob of allBlobs) {
      blobMap.set(blob.id, blob);
    }

    return messages.map((m) => {
      if (m.role !== 'user') return m;
      let result = m;

      if (m.images?.length) {
        result = {
          ...result,
          images: m.images.map((img): ImageAttachment => {
            const base: ImageAttachment = { ...img, kind: 'image' };
            if (base.dataUrl) return base;
            const blob = blobMap.get(base.id);
            return {
              ...base,
              dataUrl: blob?.dataUrl ?? MISSING_IMAGE_PLACEHOLDER,
            };
          }),
        };
      }

      if (m.attachments?.length) {
        result = {
          ...result,
          attachments: m.attachments.map((att): Attachment => {
            // Infer kind from shape if missing (defensive for stored data)
            const kind =
              att.kind ??
              ('dataUrl' in att ? ('image' as const) : ('file' as const));

            if (kind === 'image') {
              const img = { ...att, kind } as ImageAttachment;
              if (!img.dataUrl) {
                const blob = blobMap.get(img.id);
                return {
                  ...img,
                  dataUrl: blob?.dataUrl ?? MISSING_IMAGE_PLACEHOLDER,
                };
              }
              return img;
            } else {
              const file = { ...att, kind } as FileAttachment;
              if (!file.textContent) {
                const blob = blobMap.get(file.id);
                return {
                  ...file,
                  textContent:
                    blob?.textContent ?? '[File content unavailable]',
                };
              }
              return file;
            }
          }),
        };
      }

      return result;
    });
  }

  async save(conv: Conversation): Promise<void> {
    const db = await this.getDB();
    const { stripped, blobs } = this.stripBlobs(conv.id, conv.messages);
    const tx = db.transaction([BLOB_STORE, STORE_NAME], 'readwrite');
    const blobStore = tx.objectStore(BLOB_STORE);
    for (const b of blobs) blobStore.put(b);
    tx.objectStore(STORE_NAME).put({ ...conv, messages: stripped });
    await tx.done;
  }

  async get(id: string): Promise<Conversation | undefined> {
    const db = await this.getDB();
    const conv = await db.get(STORE_NAME, id);
    if (!conv) return undefined;
    conv.messages = await this.hydrateBlobs(db, id, conv.messages);
    return conv;
  }

  async delete(id: string): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction([BLOB_STORE, STORE_NAME], 'readwrite');
    // Delete associated blobs
    const blobStore = tx.objectStore(BLOB_STORE);
    const idx = blobStore.index('conversationId');
    let cursor = await idx.openCursor(id);
    while (cursor) {
      cursor.delete();
      cursor = await cursor.continue();
    }
    // Delete the conversation
    tx.objectStore(STORE_NAME).delete(id);
    await tx.done;
  }

  async list(projectKey?: string): Promise<ConversationSummary[]> {
    const db = await this.getDB();
    let all: Conversation[];
    if (projectKey) {
      all = await db.getAllFromIndex(STORE_NAME, 'projectKey', projectKey);
    } else {
      all = await db.getAll(STORE_NAME);
    }
    return all
      .map(({ messages: _, ...summary }) => summary)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }
}

/** Default store instance — swap this to change the persistence layer. */
let activeStore: ChatHistoryStore = new IDBChatHistoryStore();

export function getChatHistoryStore(): ChatHistoryStore {
  return activeStore;
}

export function setChatHistoryStore(store: ChatHistoryStore): void {
  activeStore = store;
}

// Convenience functions that delegate to the active store.
// These preserve the existing API so consumers don't need to change.
export async function saveConversation(conv: Conversation): Promise<void> {
  return activeStore.save(conv);
}

export async function getConversation(
  id: string,
): Promise<Conversation | undefined> {
  return activeStore.get(id);
}

export async function deleteConversation(id: string): Promise<void> {
  return activeStore.delete(id);
}

export async function listConversations(
  projectKey?: string,
): Promise<ConversationSummary[]> {
  return activeStore.list(projectKey);
}
